import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';

import {
  getRestrictedCountriesForIssuer,
  isIssuerRestrictedInCountry,
} from '@/lib/geo/restrictedIssuers';

import { type Issuer, isRwaIssuer } from '@/utils/0x/token-registry.types';

export const geoCheckQueryKey = () => ['geo-check'] as const;

/** Fetches the user's country and shares it through the query cache. */
export const useGeoCheck = () =>
  useQuery({
    queryKey: geoCheckQueryKey(),
    queryFn: async () => {
      const res = await fetch('/api/geo-check');
      if (!res.ok) throw new Error(`Geo check failed: ${res.statusText}`);
      const data = (await res.json()) as { country: string };
      return data;
    },
    refetchOnReconnect: false,
  });

export type TradeGeoStatus = 'geo-checking' | 'geo-allowed' | 'geo-blocked';

export const useTradeGeoStatus = (buyIssuer?: Issuer, sellIssuer?: Issuer): TradeGeoStatus => {
  const { data, isSuccess } = useGeoCheck();

  return useMemo(() => {
    const issuers = [buyIssuer, sellIssuer].filter(
      (issuer): issuer is NonNullable<Issuer> => issuer != null,
    );
    const requiresGeoCheck = issuers.some(
      (issuer) => isRwaIssuer(issuer) || getRestrictedCountriesForIssuer(issuer).length > 0,
    );
    if (!requiresGeoCheck) return 'geo-allowed';
    if (!isSuccess || !data) return 'geo-checking';

    return issuers.some((issuer) => isIssuerRestrictedInCountry(issuer, data.country))
      ? 'geo-blocked'
      : 'geo-allowed';
  }, [buyIssuer, data, isSuccess, sellIssuer]);
};
